import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { Clock, MapPin, UserCheck, Users, MessageCircle, X, Send, BookOpen } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export default function BookClub() {
  const { user, profile, isPending } = useAuth();
  const navigate = useNavigate();
  const [currentBook, setCurrentBook] = useState(null);
  const [nextVenue, setNextVenue] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rsvps, setRsvps] = useState([]);
  const [hasRsvped, setHasRsvped] = useState(false);
  const [showRsvpModal, setShowRsvpModal] = useState(false);
  const [showChatModal, setShowChatModal] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const chatScrollRef = useRef(null);

  const fetchData = useCallback(async () => {
    try {
      const [booksResult, venuesResult] = await Promise.all([
        supabase.from('book_club_books').select('*').eq('is_current', true).maybeSingle(),
        supabase.from('book_club_venues').select('*').eq('is_next', true).order('meeting_date', { ascending: true }).maybeSingle(),
      ]);

      if (booksResult.data) setCurrentBook(booksResult.data);
      if (venuesResult.data) {
        setNextVenue(venuesResult.data);
        if (user) await fetchRsvps(venuesResult.data.id);
      }
    } catch (error) {
      console.error('Error fetching book club data:', error);
    } finally {
      setLoading(false);
    }
  }, [user]);

  const fetchRsvps = async (venueId) => {
    const { data: rsvpList } = await supabase.from('book_club_rsvps').select('id, user_id').eq('venue_id', venueId);
    if (rsvpList && rsvpList.length > 0) {
      const userIds = rsvpList.map(r => r.user_id);
      const { data: profiles } = await supabase.from('profiles').select('user_id, full_name, headshot_url, role, company').in('user_id', userIds);
      const profileMap = new Map(profiles?.map(p => [p.user_id, p]) || []);
      setRsvps(rsvpList.map(rsvp => ({ ...rsvp, profiles: profileMap.get(rsvp.user_id) || null })).filter(rsvp => rsvp.profiles !== null));
      setHasRsvped(rsvpList.some(rsvp => rsvp.user_id === user?.id));
    } else {
      setRsvps([]);
      setHasRsvped(false);
    }
  };

  const handleRsvp = async () => {
    if (!user || !nextVenue) return;
    
    // Find the synced event for this venue
    const { data: syncedEvent } = await supabase
      .from('events')
      .select('id')
      .eq('book_club_venue_id', nextVenue.id)
      .maybeSingle();
    
    if (hasRsvped) {
      // Remove RSVP from book club
      await supabase.from('book_club_rsvps').delete().eq('venue_id', nextVenue.id).eq('user_id', user.id);
      // Also remove from synced event
      if (syncedEvent) {
        await supabase.from('event_rsvps').delete().eq('event_id', syncedEvent.id).eq('user_id', user.id);
      }
      setHasRsvped(false);
    } else {
      // Add RSVP to book club
      await supabase.from('book_club_rsvps').insert({ venue_id: nextVenue.id, user_id: user.id });
      // Also add to synced event
      if (syncedEvent) {
        await supabase.from('event_rsvps').insert({ event_id: syncedEvent.id, user_id: user.id });
      }
      setHasRsvped(true);
    }
    await fetchRsvps(nextVenue.id);
  };

  const fetchChatMessages = async (venueId) => {
    const { data: messages } = await supabase.from('book_club_chat_messages').select('id, user_id, message, created_at').eq('venue_id', venueId).order('created_at', { ascending: true });
    if (messages && messages.length > 0) {
      const userIds = [...new Set(messages.map(m => m.user_id))];
      const { data: profiles } = await supabase.from('profiles').select('user_id, full_name, headshot_url').in('user_id', userIds);
      const profileMap = new Map(profiles?.map(p => [p.user_id, p]) || []);
      setChatMessages(messages.map(msg => ({ ...msg, profiles: profileMap.get(msg.user_id) || null })).filter(msg => msg.profiles !== null));
    } else {
      setChatMessages([]);
    }
  };

  const sendChatMessage = async () => {
    if (!newMessage.trim() || !nextVenue || !user || sendingMessage) return;
    setSendingMessage(true);
    await supabase.from('book_club_chat_messages').insert({ venue_id: nextVenue.id, user_id: user.id, message: newMessage.trim() });
    setNewMessage('');
    setTimeout(async () => { await fetchChatMessages(nextVenue.id); setSendingMessage(false); }, 300);
  };

  const openChatModal = () => {
    if (!nextVenue) return;
    setShowChatModal(true);
    fetchChatMessages(nextVenue.id);
  };

  useEffect(() => { fetchData(); }, [fetchData]);

  const formatDate = (dateString) => {
    // Parse date as local time to avoid timezone shift
    const [year, month, day] = dateString.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  };
  const formatTime = (timeString) => {
    const [hours, minutes] = timeString.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    return `${hour % 12 || 12}:${minutes} ${ampm}`;
  };

  if (loading) {
    return <div className="flex items-center justify-center min-h-[400px]"><div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-brand-gold"></div></div>;
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Hero Section */}
      <div className="glass-card rounded-3xl p-8 mb-10 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-brand-gold/10 to-transparent" />
        <div className="relative z-10">
          <div className="flex items-center gap-2 mb-3">
            <BookOpen size={20} className="text-brand-gold" />
            <span className="text-brand-gold font-semibold text-sm uppercase tracking-wider">Weekly Discussions</span>
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-4">Book Club</h1>
          <p className="text-gray-400 leading-relaxed max-w-2xl mb-6">
            Join fellow members for weekly book discussions featuring complimentary coffee, intentional networking, and engaging conversations with like-minded professionals.
          </p>
          <div className="flex flex-wrap gap-4 text-sm">
            <div className="flex items-center gap-2 px-4 py-2 rounded-xl bg-white/5 border border-white/10">
              <Users size={16} className="text-brand-gold" />
              <span className="text-gray-300">Intentional Networking</span>
            </div>
          </div>
        </div>
      </div>

      {/* Current Book */}
      {currentBook ? (
        <div className="mb-8">
          <h2 className="text-xl font-bold text-brand-gold mb-4">Currently Reading:</h2>
          <div className="bg-white/5 border border-brand-gold-border rounded-2xl p-6" data-testid="current-book-card">
            {currentBook.photo_url ? (
              <img src={currentBook.photo_url} alt={currentBook.title} className="w-full max-w-xs mx-auto h-auto rounded-xl mb-6 bg-brand-gold-light" />
            ) : (
              <div className="w-full max-w-xs mx-auto h-64 rounded-xl mb-6 bg-brand-gold-light flex items-center justify-center text-gray-500">No Cover</div>
            )}
            <h3 className="text-2xl font-bold text-white mb-2">{currentBook.title}</h3>
            <p className="text-gray-400 mb-4">by {currentBook.author}</p>
            <span className="inline-block px-3 py-1 bg-brand-gold-light rounded-lg text-sm font-semibold text-brand-gold mb-4">{currentBook.chapters_per_week}-{currentBook.chapters_per_week + 1} chapters/week</span>
            <p className="text-gray-400 text-sm leading-relaxed mb-3">The Book Club meets weekly to discuss 2-3 chapters. This is a great opportunity for busy professionals to expand their knowledge while connecting with fellow members.</p>
            <p className="text-brand-gold text-sm font-semibold">Join us at our next book club meeting (see time and location details below)</p>
          </div>
        </div>
      ) : (
        <div className="text-center py-12 mb-8">
          <h3 className="text-xl font-bold text-brand-gold mb-2">No Current Book</h3>
          <p className="text-gray-400">Check back soon for the next book selection</p>
        </div>
      )}

      {/* Next Meeting */}
      {nextVenue ? (
        <div>
          <h2 className="text-xl font-bold text-brand-gold mb-4">Next Meeting</h2>
          <div className="bg-white/5 border border-brand-gold-border rounded-2xl overflow-hidden" data-testid="next-venue-card">
            {nextVenue.image_url && (
              <div className="relative bg-black/30">
                <img src={nextVenue.image_url} alt={nextVenue.venue_name} className="w-full h-auto max-h-64 object-contain mx-auto" />
              </div>
            )}
            <div className="p-6">
              <h3 className="text-2xl font-bold text-white mb-4">{nextVenue.venue_name}</h3>
              <div className="space-y-3 mb-4 pb-4 border-b border-brand-gold-border">
                <div className="flex items-center text-gray-300"><MapPin size={20} className="text-brand-gold mr-3" />{nextVenue.address || 'Location TBA'}</div>
                <div className="flex items-center text-gray-300"><Clock size={20} className="text-brand-gold mr-3" />{formatDate(nextVenue.meeting_date)} • {formatTime(nextVenue.meeting_time)}</div>
              </div>
              <p className="text-gray-400 text-sm mb-6">Enjoy coffee and great discussion with fellow book club members. New members are always welcome!</p>

              {/* Show attending count only to signed-in members */}
              {user && nextVenue && (
                <div className="flex items-center gap-2 mb-4 text-sm">
                  <Users size={16} className="text-brand-gold" />
                  <span className="text-gray-300">
                    <span className="font-bold text-white">{rsvps.length}</span> attending
                  </span>
                </div>
              )}

              {!user && (
                <button onClick={() => navigate('/auth')} className="w-full py-4 bg-brand-gold text-brand-dark font-bold rounded-xl hover:bg-yellow-400 transition-colors" data-testid="signin-btn">
                  Sign in to RSVP and view full event details
                </button>
              )}

              {user && isPending && (
                <div className="w-full py-4 bg-brand-gold-light border border-brand-gold-border rounded-xl text-center">
                  <span className="text-gray-400 font-semibold">RSVP Available after approval</span>
                </div>
              )}

            {/* Allow ALL approved members to RSVP (profile exists and not pending) */}
            {user && profile && !isPending && (
              <div className="flex flex-wrap gap-3">
                <button onClick={handleRsvp} className={`flex items-center gap-2 px-4 py-2 rounded-lg border font-semibold transition-colors ${hasRsvped ? 'bg-brand-gold border-brand-gold text-brand-dark' : 'bg-brand-gold-light border-brand-gold-border text-brand-gold hover:bg-brand-gold/20'}`} data-testid="rsvp-btn">
                  <UserCheck size={18} />{hasRsvped ? 'Going' : 'RSVP'}
                </button>
                <button onClick={() => setShowRsvpModal(true)} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-brand-gold-light border border-brand-gold-border text-brand-gold font-semibold hover:bg-brand-gold/20 transition-colors" data-testid="attendees-btn">
                  <Users size={18} />{rsvps.length}
                </button>
                <button onClick={openChatModal} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-brand-gold-light border border-brand-gold-border text-brand-gold font-semibold hover:bg-brand-gold/20 transition-colors" data-testid="chat-btn">
                  <MessageCircle size={18} />Chat
                </button>
              </div>
            )}
            </div>
          </div>
        </div>
      ) : (
        <div className="text-center py-12">
          <h3 className="text-xl font-bold text-brand-gold mb-2">No Upcoming Meetings</h3>
          <p className="text-gray-400">The next meeting location will be announced soon</p>
        </div>
      )}

      {/* RSVP Modal */}
      {showRsvpModal && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-end sm:items-center justify-center" data-testid="book-rsvp-modal">
          <div className="bg-brand-dark-light rounded-t-3xl sm:rounded-2xl w-full max-w-md max-h-[80vh] overflow-hidden">
            <div className="flex items-center justify-between p-6 border-b border-brand-gold-border">
              <h3 className="text-xl font-bold text-brand-gold">Attendees ({rsvps.length})</h3>
              <button onClick={() => setShowRsvpModal(false)}><X size={24} className="text-brand-gold" /></button>
            </div>
            <div className="p-6 overflow-y-auto max-h-96">
              {rsvps.map((rsvp) => (
                <div key={rsvp.id} className="flex items-center py-3 border-b border-brand-gold-border/30">
                  {rsvp.profiles?.headshot_url ? <img src={rsvp.profiles.headshot_url} alt="" className="w-12 h-12 rounded-full mr-4 object-cover" /> : <div className="w-12 h-12 rounded-full mr-4 bg-brand-gold-light flex items-center justify-center text-brand-gold font-bold">{rsvp.profiles?.full_name?.[0] || '?'}</div>}
                  <div><p className="font-semibold text-white">{rsvp.profiles?.full_name}</p><p className="text-sm text-gray-400">{rsvp.profiles?.role} {rsvp.profiles?.company ? `at ${rsvp.profiles.company}` : ''}</p></div>
                </div>
              ))}
              {rsvps.length === 0 && <p className="text-gray-400 text-center py-8">No RSVPs yet. Be the first!</p>}
            </div>
          </div>
        </div>
      )}

      {/* Chat Modal */}
      {showChatModal && nextVenue && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-end sm:items-center justify-center" data-testid="book-chat-modal">
          <div className="bg-brand-dark-light rounded-t-3xl sm:rounded-2xl w-full max-w-md max-h-[80vh] flex flex-col">
            <div className="flex items-center justify-between p-6 border-b border-brand-gold-border">
              <h3 className="text-xl font-bold text-brand-gold">Book Club Chat</h3>
              <button onClick={() => setShowChatModal(false)}><X size={24} className="text-brand-gold" /></button>
            </div>
            <div ref={chatScrollRef} className="flex-1 p-6 overflow-y-auto space-y-4 max-h-80">
              {chatMessages.map((msg) => (
                <div key={msg.id} className={msg.user_id === user?.id ? 'ml-auto' : ''}>
                  {msg.user_id !== user?.id && (
                    <div className="flex items-center gap-2 mb-1">
                      {msg.profiles?.headshot_url ? <img src={msg.profiles.headshot_url} alt="" className="w-6 h-6 rounded-full" /> : <div className="w-6 h-6 rounded-full bg-brand-gold-light flex items-center justify-center text-brand-gold text-xs font-bold">{msg.profiles?.full_name?.[0] || '?'}</div>}
                      <span className="text-xs text-brand-gold font-semibold">{msg.profiles?.full_name}</span>
                    </div>
                  )}
                  <div className={`max-w-xs px-4 py-2 rounded-2xl ${msg.user_id === user?.id ? 'bg-brand-gold text-brand-dark ml-auto' : 'bg-white/10 text-white'}`}>
                    <p className="text-sm">{msg.message}</p>
                    <p className={`text-xs mt-1 ${msg.user_id === user?.id ? 'text-brand-dark/60' : 'text-gray-400'}`}>{new Date(msg.created_at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}</p>
                  </div>
                </div>
              ))}
              {chatMessages.length === 0 && <p className="text-gray-400 text-center py-8">No messages yet. Start the conversation!</p>}
            </div>
            <div className="p-4 border-t border-brand-gold-border flex gap-3">
              <input value={newMessage} onChange={(e) => setNewMessage(e.target.value)} placeholder="Type a message..." className="flex-1 px-4 py-3 bg-black/30 rounded-xl text-white placeholder-gray-500 focus:outline-none" />
              <button onClick={sendChatMessage} disabled={!newMessage.trim() || sendingMessage} className="w-11 h-11 bg-brand-gold rounded-full flex items-center justify-center disabled:opacity-40"><Send size={18} className="text-brand-dark" /></button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
